import { environment } from '../../environments/environment';

/** سكوبات الموظفين/الأدمن — وجود أيٍّ منها يعني أنّ المستخدم ليس مستخدم بوابة خارجية بحتة. */
const STAFF_SCOPES = ['curalab', 'lab', 'hr', 'insurance'];

/** البوابات الخارجية الثلاث التي يجب تحويل مستخدميها دائماً إلى تطبيقهم. */
const PORTAL_SCOPES = ['patient', 'doctor', 'referrallab'];

function normalizeList(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map((x) => String(x));
  if (typeof raw === 'string') return raw.split(/\s+/).filter(Boolean);
  return [];
}

/**
 * يحدّد ما إذا كانت مطالبات التوكن تعود لمستخدم بوابة خارجية بحتة
 * (مريض/طبيب/مختبر إحالة) وليس موظفاً/أدمن. يُرجع مفتاح البوابة أو null.
 *
 * المعيار: السكوب يحوي سكوب بوابة واحدة فقط ولا يحوي أي سكوب موظفين.
 */
export function detectPortalOnlyKey(claims: any): string | null {
  if (!claims) return null;

  const perms = normalizeList(claims.perm);
  // الأدمن/مالك النظام لا يُحوَّل أبداً — يستخدم البوابة نفسها.
  if (perms.includes('system:*') || perms.includes('auth:*')) return null;

  // النطاقات الممنوحة قد تأتي تحت "scope" (نص مفصول بمسافات حسب RFC 9068)
  // أو "scp"، أو تحت مطالبة OpenIddict الداخلية "oi_scp" (مصفوفة) — اقرأ الثلاثة.
  const scopes = normalizeList(claims.scope ?? claims.scp ?? claims.oi_scp);
  // أي سكوب موظفين ⇒ ليس مستخدم بوابة بحت.
  if (scopes.some((s) => STAFF_SCOPES.includes(s))) return null;

  const portalScopes = PORTAL_SCOPES.filter((s) => scopes.includes(s));
  if (portalScopes.length === 1) return portalScopes[0];

  return null;
}

/**
 * يبني رابط بوابة خارجية من مفتاحها المعروف (patient/doctor/referrallab) مع
 * إلحاق التوكن في الـ fragment (نفس صيغة مُشغِّل التطبيقات: `/#/?authToken=...`)،
 * أو null إذا كان المفتاح فارغاً أو لم يُكوَّن رابط له في systemUrls.
 *
 * يُستخدم مع القرار القادم من الخادم (`/account/login` → portalKey) المبني على
 * حقيقة قاعدة البيانات (رتبة المستخدم/ارتباطه) لا على سكوبات التوكن.
 */
export function buildPortalUrlForKey(
  key: string | null | undefined,
  accessToken: string | null
): string | null {
  if (!key) return null;

  const raw = environment.systemUrls?.[key];
  if (!raw) return null;

  // إزالة # و ? ثم الشرطات الزائدة (نفس منطق مُشغِّل التطبيقات).
  const base = (raw.split('#')[0] ?? raw).split('?')[0] ?? raw;
  const normalized = base.replace(/\/+$/, '');
  if (!normalized) return null;

  return accessToken
    ? `${normalized}/#/?authToken=${encodeURIComponent(accessToken)}`
    : `${normalized}/`;
}

/**
 * يبني رابط بوابة المستخدم الخارجية بالاعتماد على مطالبات التوكن (fallback عند
 * غياب قرار الخادم)، أو null إذا لم يكن مستخدم بوابة بحتاً.
 */
export function resolvePortalHomeUrl(
  claims: any,
  accessToken: string | null
): string | null {
  return buildPortalUrlForKey(detectPortalOnlyKey(claims), accessToken);
}
